import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { UserContext } from '../UserContext';
import { useTranslation } from '../LanguageContext';
import CampaignForm, { PAYMENT_MODELS, PlatformOptions, platformLabel } from '../components/CampaignForm';
import DealCenter, { StatusPill } from '../components/DealCenter';
import DepositModal from '../components/DepositModal';
import ApplicantKit from '../components/ApplicantKit';
import CampaignCalendar from '../components/CampaignCalendar';
import CreatorDiscovery, { CampaignSuggestions } from '../components/CreatorDiscovery';
import { ReputationBadge } from '../components/ReputationBadge';
import { AvailabilityChip } from '../components/AvailabilityPanel';
import CreatorOpportunities from '../components/CreatorOpportunities';
import ContractsList from '../components/ContractsList';
import { filledSlots } from '../utils/showcaseFill';

const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric',
    });
};

const usd = (value) => `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

/** Compact form for the card badges — $1.2k reads better than $1,200.00. */
const usdShort = (value) => {
    const n = Number(value || 0);
    if (n >= 1000) return `$${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
    return `$${n % 1 === 0 ? n : n.toFixed(2)}`;
};

function Avatar({ user, size = 'w-9 h-9' }) {
    return (
        <div className="relative flex-shrink-0">
            <img src={user?.profile_picture || '/logo192.png'} alt={user?.username}
                className={`${size} rounded-full object-cover ring-2 ring-white shadow-sm`} />
            {user?.verified && (
                <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-primary-600 rounded-full flex items-center justify-center ring-2 ring-white">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                </span>
            )}
        </div>
    );
}

function BudgetBadge({ campaign, t }) {
    const { payment_model, budget_usd, commission_percent, barter_value } = campaign;
    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {(payment_model === 'fixed' || payment_model === 'hybrid') && budget_usd > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gradient-to-br from-[#e6fcf9] to-[#b2f0e8] border border-[#20c9b5]/30">
                    <span className="text-sm font-black text-[#0f8a7e] tabular-nums">{usdShort(budget_usd)}</span>
                </span>
            )}
            {(payment_model === 'affiliate' || payment_model === 'hybrid') && commission_percent > 0 && (
                <span className="px-2 py-1 rounded-lg bg-violet-50 text-violet-700 text-[11px] font-bold border border-violet-200">
                    %{commission_percent} {t('campaigns.commission')}
                </span>
            )}
            {(payment_model === 'barter' || payment_model === 'hybrid') && barter_value && (
                <span className="px-2 py-1 rounded-lg bg-amber-50 text-amber-700 text-[11px] font-bold border border-amber-200">
                    {t('campaigns.payment_barter')}
                </span>
            )}
        </div>
    );
}

function CampaignCard({ campaign, onOpen, t }) {
    const cover = campaign.cover_image || campaign.images?.[0]?.url;

    return (
        <button onClick={() => onOpen(campaign)}
            className="text-left w-full rounded-2xl bg-white border border-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
            {/* Cover. The frame is 16:9 because that is the shape the upload
                cropper hands out — a fixed height would crop the crop again,
                and the brand would be back to not knowing what it published. */}
            <div className="relative aspect-[16/9] bg-gradient-to-br from-slate-100 to-slate-200">
                {cover
                    ? <img src={cover} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-slate-300">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                        </svg>
                    </div>}

                <span className={`absolute top-2 left-2 px-2 py-1 rounded-lg text-[10px] font-bold border backdrop-blur-sm ${campaign.is_open
                    ? 'bg-emerald-500/90 text-white border-emerald-400'
                    : 'bg-slate-700/80 text-white border-slate-600'}`}>
                    {campaign.is_open ? t('campaigns.open') : t('campaigns.closed')}
                </span>

                {campaign.images?.length > 1 && (
                    <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded-md bg-black/50 text-white text-[10px] font-semibold">
                        1/{campaign.images.length}
                    </span>
                )}

                <div className="absolute bottom-2 right-2">
                    <BudgetBadge campaign={campaign} t={t} />
                </div>
            </div>

            {/* Body */}
            <div className="p-3.5">
                <div className="flex items-center gap-1.5 mb-2">
                    <Avatar user={campaign.brand} size="w-7 h-7" />
                    <span className="text-xs font-semibold text-slate-600 truncate">{campaign.brand?.username}</span>
                    {/* How this brand has actually behaved. Worth more to a
                        creator deciding whether to apply than anything else
                        on the card. */}
                    <ReputationBadge rating={campaign.brand?.rating} compact />
                    <span className="text-[10px] text-slate-400 ml-auto flex-shrink-0">{formatDate(campaign.created_at)}</span>
                </div>

                <h3 className="text-sm font-bold text-slate-800 leading-snug line-clamp-2">{campaign.title}</h3>
                {campaign.summary && (
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2 leading-relaxed">{campaign.summary}</p>
                )}

                {campaign.platforms?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                        {campaign.platforms.slice(0, 3).map(platform => (
                            <span key={platform} className="px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[10px] font-semibold">
                                {platformLabel(platform)}
                            </span>
                        ))}
                    </div>
                )}

                <div className="flex items-center gap-3 mt-3 pt-2.5 border-t border-slate-100 text-[11px] text-slate-500">
                    <span className="flex items-center gap-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0" />
                        </svg>
                        <strong className="text-slate-700">{campaign.applicant_count}</strong> {t('campaigns.applicants')}
                    </span>
                    <span className="flex items-center gap-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <strong className="text-slate-700">{filledSlots(campaign)}</strong>/{campaign.slots}
                    </span>
                    {campaign.my_application && (
                        <span className="ml-auto px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 font-bold border border-primary-100">
                            {t(`campaigns.appStatus_${campaign.my_application.status}`)}
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
}

function Gallery({ images, cover }) {
    const list = images?.length ? images : (cover ? [{ id: 'cover', url: cover }] : []);
    const [index, setIndex] = useState(0);
    if (!list.length) return null;

    return (
        <div className="relative h-52 sm:h-64 bg-slate-900 overflow-hidden">
            {/* The detail sheet is wider than 16:9 on a desktop, so filling it
                would slice the top and bottom off a crop the brand chose
                deliberately. The whole frame is shown and its own blurred copy
                fills the leftover width. */}
            <img src={list[index]?.url} alt="" aria-hidden
                className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl opacity-50" />
            <img src={list[index]?.url} alt="" className="relative w-full h-full object-contain" />
            {list.length > 1 && (
                <>
                    <button onClick={() => setIndex(i => (i - 1 + list.length) % list.length)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition">‹</button>
                    <button onClick={() => setIndex(i => (i + 1) % list.length)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition">›</button>
                    <div className="absolute bottom-2 inset-x-0 flex justify-center gap-1.5">
                        {list.map((image, i) => (
                            <button key={image.id} onClick={() => setIndex(i)}
                                className={`h-1.5 rounded-full transition-all ${i === index ? 'w-5 bg-white' : 'w-1.5 bg-white/50'}`} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function DetailRow({ label, value }) {
    if (value === null || value === undefined || value === '' || value === 0) return null;
    return (
        <div className="flex items-start justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
            <span className="text-xs text-slate-500 flex-shrink-0">{label}</span>
            <span className="text-xs font-semibold text-slate-800 text-right">{value}</span>
        </div>
    );
}

/**
 * Everything a campaign says about itself, minus the title block and the apply
 * bar: counters, platforms, the brand's own brief sections, deliverables, KPIs
 * and the terms table.
 *
 * Split out of `CampaignDetail` because a creator reading their own
 * applications should not have to reopen the modal to remember what they
 * signed up for — the applied tab embeds this directly under each row. One
 * copy, so the two surfaces cannot describe the same campaign differently.
 */
function CampaignBrief({ campaign, t }) {
    return (
        <>
            {/* Counters */}
            <div className="grid grid-cols-3 gap-2">
                <div className="rounded-xl bg-white border border-slate-200 p-2.5 text-center">
                    <p className="text-lg font-black text-slate-800">{campaign.applicant_count}</p>
                    <p className="text-[10px] text-slate-500 font-semibold">{t('campaigns.applicants')}</p>
                </div>
                <div className="rounded-xl bg-white border border-slate-200 p-2.5 text-center">
                    <p className="text-lg font-black text-emerald-600">{filledSlots(campaign)}</p>
                    <p className="text-[10px] text-slate-500 font-semibold">{t('campaigns.approved')}</p>
                </div>
                <div className="rounded-xl bg-white border border-slate-200 p-2.5 text-center">
                    <p className="text-lg font-black text-slate-800">{campaign.slots}</p>
                    <p className="text-[10px] text-slate-500 font-semibold">{t('campaigns.slots')}</p>
                </div>
            </div>

            {/* Platforms */}
            {campaign.platforms?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {campaign.platforms.map(platform => (
                        <span key={platform} className="px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-600 text-[11px] font-semibold">
                            {platformLabel(platform)}
                        </span>
                    ))}
                </div>
            )}

            {/* The brand's own brief sections */}
            {campaign.sections?.map(section => (
                <section key={section.id} className="rounded-2xl bg-white border border-slate-200 p-4">
                    <h2 className="text-base font-black text-slate-900 mb-1.5">{section.title}</h2>
                    <p className="text-sm text-slate-600 whitespace-pre-line leading-relaxed">{section.body}</p>
                </section>
            ))}

            {/* Deliverables */}
            {campaign.deliverables?.length > 0 && (
                <section className="rounded-2xl bg-white border border-slate-200 p-4">
                    <h2 className="text-base font-black text-slate-900 mb-2">{t('campaigns.deliverablesTitle')}</h2>
                    <ul className="space-y-1.5">
                        {campaign.deliverables.map((row, index) => (
                            <li key={index} className="flex items-center gap-2 text-sm text-slate-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                                <strong>{row.quantity || 1}×</strong>
                                {row.format ? t(`campaigns.format_${row.format}`) : ''}
                                {row.platform && <span className="text-slate-400">· {platformLabel(row.platform)}</span>}
                                {row.note && <span className="text-slate-400">— {row.note}</span>}
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* KPIs */}
            {campaign.kpis?.length > 0 && (
                <section className="rounded-2xl bg-white border border-slate-200 p-4">
                    <h2 className="text-base font-black text-slate-900 mb-2">{t('campaigns.kpiTitle')}</h2>
                    <div className="grid grid-cols-2 gap-2">
                        {campaign.kpis.map((row, index) => (
                            <div key={index} className="rounded-xl bg-slate-50 border border-slate-100 p-2.5">
                                <p className="text-[11px] text-slate-500">{row.metric}</p>
                                <p className="text-sm font-black text-slate-800">{row.target}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Terms */}
            <section className="rounded-2xl bg-white border border-slate-200 p-4">
                <h2 className="text-base font-black text-slate-900 mb-1">{t('campaigns.termsTitle')}</h2>
                <DetailRow label={t('campaigns.fieldPaymentTerms')}
                    value={campaign.payment_terms_days ? `${t('campaigns.netDays')} ${campaign.payment_terms_days}` : null} />
                <DetailRow label={t('campaigns.fieldApplicationDeadline')} value={campaign.application_deadline ? formatDate(campaign.application_deadline) : null} />
                <DetailRow label={t('campaigns.fieldDraftDue')} value={campaign.draft_due_date ? formatDate(campaign.draft_due_date) : null} />
                <DetailRow label={t('campaigns.fieldPublishDate')} value={campaign.publish_date ? formatDate(campaign.publish_date) : null} />
                <DetailRow label={t('campaigns.fieldRevisionLimit')} value={campaign.revision_limit} />
                <DetailRow label={t('campaigns.fieldAutoApprove')} value={`${campaign.auto_approve_hours} ${t('campaigns.hours')}`} />
                <DetailRow label={t('campaigns.fieldUsageRights')}
                    value={campaign.usage_rights_months ? `${campaign.usage_rights_months} ${t('campaigns.months')}` : null} />
                <DetailRow label={t('campaigns.fieldExclusivity')}
                    value={campaign.exclusivity_days ? `${campaign.exclusivity_days} ${t('campaigns.days')}` : null} />
                <DetailRow label={t('campaigns.fieldExclusivityNote')} value={campaign.exclusivity_note} />
                <DetailRow label={t('campaigns.fieldMinFollowers')} value={campaign.min_followers || null} />
                <DetailRow label={t('campaigns.fieldDiscountCode')} value={campaign.discount_code} />
                {campaign.utm_link && (
                    <div className="flex items-start justify-between gap-4 py-2">
                        <span className="text-xs text-slate-500">{t('campaigns.fieldUtm')}</span>
                        <a href={campaign.utm_link} target="_blank" rel="noopener noreferrer"
                            className="text-xs font-semibold text-primary-700 hover:underline text-right break-all">
                            {campaign.utm_link}
                        </a>
                    </div>
                )}
            </section>

            {/* Escrow reassurance */}
            <div className="rounded-2xl bg-primary-50 border border-primary-100 p-3.5 flex gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="text-xs text-primary-900 leading-relaxed">{t('campaigns.escrowExplain')}</p>
            </div>

            {campaign.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {campaign.tags.map(tag => (
                        <span key={tag.id || tag} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-semibold">
                            #{tag.name || tag}
                        </span>
                    ))}
                </div>
            )}
        </>
    );
}

function CampaignDetail({ campaign, me, onClose, onChanged }) {
    const { t } = useTranslation();
    const [applying, setApplying] = useState(false);
    const [message, setMessage] = useState('');
    const [proposed, setProposed] = useState('');
    const [myRates, setMyRates] = useState(null);
    const [showApply, setShowApply] = useState(false);
    const [error, setError] = useState('');

    const isOwner = campaign.brand?.id === me.id;
    const isCreator = me.account_type === 'creator';
    const application = campaign.my_application;

    // The creator's own prices, pulled in only once they start an
    // application — quoting from the rate card is the whole point of having
    // published one.
    useEffect(() => {
        if (!showApply || myRates || !isCreator) return;
        api.get('/api/rate-card/')
            .then(res => setMyRates(res.data))
            .catch(() => setMyRates({ items: [] }));
    }, [showApply, myRates, isCreator]);

    const apply = async () => {
        setApplying(true);
        setError('');
        try {
            const res = await api.post(`/api/campaigns/${campaign.id}/apply/`, {
                message,
                proposed_usd: proposed === '' ? null : Number(proposed),
            });
            if (res.status === 201) { setShowApply(false); onChanged(); }
        } catch (err) {
            setError(err.response?.data?.error || t('campaigns.saveError'));
        } finally { setApplying(false); }
    };

    const withdraw = async () => {
        setApplying(true);
        try {
            await api.delete(`/api/campaigns/${campaign.id}/apply/`);
            onChanged();
        } catch (err) {
            setError(err.response?.data?.error || t('campaigns.saveError'));
        } finally { setApplying(false); }
    };

    return (
        <div className="fixed inset-0 z-[9000] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-slate-50 w-full sm:max-w-2xl h-[92vh] sm:h-[88vh] rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden">

                <div className="relative flex-shrink-0">
                    <Gallery images={campaign.images} cover={campaign.cover_image} />
                    <button onClick={onClose}
                        className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-6 py-5 space-y-4">
                    {/* Title block */}
                    <div>
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                            <span className={`px-2 py-1 rounded-lg text-[10px] font-bold ${campaign.is_open
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-slate-100 text-slate-500 border border-slate-200'}`}>
                                {campaign.is_open ? t('campaigns.open') : t('campaigns.closed')}
                            </span>
                            <span className="px-2 py-1 rounded-lg text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200">
                                {t(`campaigns.payment_${campaign.payment_model}`)}
                            </span>
                            <span className="text-[11px] text-slate-400 ml-auto">{formatDate(campaign.created_at)}</span>
                        </div>

                        <h1 className="text-xl font-black text-slate-900 leading-tight">{campaign.title}</h1>
                        {campaign.summary && <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{campaign.summary}</p>}

                        <div className="flex items-center gap-2.5 mt-3">
                            <Avatar user={campaign.brand} />
                            <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                    <p className="text-sm font-bold text-slate-800 truncate">{campaign.brand?.username}</p>
                                    <ReputationBadge rating={campaign.brand?.rating} />
                                </div>
                                <p className="text-[11px] text-slate-400">{t('campaigns.brandAccount')}</p>
                            </div>
                            <div className="ml-auto text-right">
                                <BudgetBadge campaign={campaign} t={t} />
                            </div>
                        </div>
                    </div>

                    <CampaignBrief campaign={campaign} t={t} />
                </div>

                {/* Action bar */}
                <div className="px-4 sm:px-6 py-4 bg-white border-t border-slate-100 flex-shrink-0">
                    {error && <p className="text-xs text-red-600 mb-2 text-center">{error}</p>}

                    {isOwner && (
                        <p className="text-sm text-slate-500 text-center">{t('campaigns.ownCampaign')}</p>
                    )}

                    {!isOwner && !isCreator && (
                        <p className="text-sm text-slate-500 text-center">{t('campaigns.creatorsOnly')}</p>
                    )}

                    {!isOwner && isCreator && !application && !showApply && (
                        <button onClick={() => setShowApply(true)} disabled={!campaign.is_open}
                            className="btn-primary w-full py-3 text-base font-bold disabled:opacity-40">
                            {campaign.is_open ? t('campaigns.applyBtn') : t('campaigns.closed')}
                        </button>
                    )}

                    {!isOwner && isCreator && showApply && (
                        <div className="space-y-2">
                            <textarea className="input min-h-[70px] resize-y text-sm" value={message}
                                placeholder={t('campaigns.applyMessagePlaceholder')}
                                onChange={(e) => setMessage(e.target.value)} />

                            {/* Quote a price. Naming a number here is what
                                stops the negotiation restarting in the DMs,
                                and the rate card makes it one tap. */}
                            <div className="flex items-center gap-2">
                                <label className="text-[11px] text-slate-500 flex-shrink-0">
                                    {t('campaigns.proposedPrice')}
                                </label>
                                <div className="relative w-32">
                                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400 pointer-events-none">$</span>
                                    <input type="number" min={0} step="0.01" value={proposed}
                                        placeholder={campaign.budget_usd || 0}
                                        onChange={(e) => setProposed(e.target.value)}
                                        className="input py-1.5 pl-6 text-sm w-full" />
                                </div>
                                {campaign.budget_usd > 0 && (
                                    <span className="text-[11px] text-slate-400">
                                        {t('campaigns.brandBudget')} {usd(campaign.budget_usd)}
                                    </span>
                                )}
                            </div>

                            {myRates?.items?.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    {myRates.items.filter(item => item.is_active !== false).map(item => (
                                        <button key={item.id} type="button"
                                            onClick={() => setProposed(item.price_usd)}
                                            className="px-2 py-1 rounded-lg bg-primary-50 text-primary-700 border border-primary-100 hover:bg-primary-100 transition text-[11px] font-semibold">
                                            {item.title} · {usd(item.price_usd)}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {myRates && !myRates.items?.length && (
                                <p className="text-[11px] text-slate-400 leading-relaxed">
                                    {t('campaigns.noRateCardHint')}
                                </p>
                            )}

                            <div className="flex gap-2">
                                <button onClick={() => setShowApply(false)} className="btn-secondary flex-1 py-2.5 text-sm">
                                    {t('campaigns.cancel')}
                                </button>
                                <button onClick={apply} disabled={applying}
                                    className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2">
                                    {applying && <span className="spinner-sm" />}
                                    {t('campaigns.sendApplication')}
                                </button>
                            </div>
                        </div>
                    )}

                    {!isOwner && isCreator && application && (
                        <div className="flex items-center gap-3">
                            <div className="flex-1">
                                <p className="text-xs text-slate-500">{t('campaigns.yourApplication')}</p>
                                <p className="text-sm font-bold text-slate-800">
                                    {t(`campaigns.appStatus_${application.status}`)}
                                </p>
                            </div>
                            {['pending', 'rejected'].includes(application.status) && (
                                <button onClick={withdraw} disabled={applying}
                                    className="px-4 py-2.5 rounded-xl text-sm font-semibold text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 transition">
                                    {t('campaigns.withdrawBtn')}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * One row in the creator's "my applications" tab.
 *
 * The row used to be a button that opened the campaign modal and nothing else,
 * which meant a creator had to reopen a dialog to check the deliverables of a
 * brief they had already applied to — the one place they are most likely to
 * want them. The brief is embedded instead, and so is the `DealCenter` for an
 * approved application: everything about one collaboration lives **inside that
 * campaign's own card**. It used to hang below the card as a sibling, which
 * made a finished deal look like a separate, permanently-open item and turned
 * a list of five applications into a page nobody could scan.
 *
 * What is open by default follows from that: a row still running (pending,
 * or a deal that is not settled yet) opens, because that is work in hand; a
 * closed one — rejected, withdrawn, completed, cancelled — starts collapsed
 * and states its outcome on the header line. The initial state is deliberately
 * computed once, so a deal that settles while the creator is reading it does
 * not fold shut under them.
 *
 * The modal stays reachable from the header — the apply/withdraw bar and the
 * image gallery live there.
 */
function AppliedRow({ application, deal, me, onOpen, onUpdated, t }) {
    // The deal's own status is the truer headline once one exists: "approved"
    // says nothing about whether the work is delivered, paid or cancelled.
    const closed = deal
        ? ['completed', 'cancelled'].includes(deal.status)
        : ['rejected', 'withdrawn'].includes(application.status);
    const [open, setOpen] = useState(!closed);
    const campaign = application.campaign_detail;

    const pillStatus = deal
        ? deal.status
        : application.status === 'approved' ? 'completed' : 'submitted';
    const pillLabel = deal
        ? t(`campaigns.dealStatus_${deal.status}`)
        : t(`campaigns.appStatus_${application.status}`);

    return (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
            <div className="flex items-center gap-3 p-3.5">
                <button onClick={() => onOpen(campaign)}
                    className="w-12 h-12 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0 hover:opacity-80 transition">
                    {campaign?.cover_image
                        ? <img src={campaign.cover_image} alt="" className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-slate-300">◎</div>}
                </button>
                <button onClick={() => setOpen(!open)} className="flex-1 min-w-0 text-left">
                    <h3 className="text-sm font-bold text-slate-800 truncate">{campaign?.title}</h3>
                    <p className="text-[11px] text-slate-400 truncate">
                        {campaign?.brand?.username} · {formatDate(application.created_at)}
                        {!deal && application.proposed_usd > 0 && <> · {usd(application.proposed_usd)}</>}
                    </p>
                    {/* The escrow line the deal panel used to carry in its own
                        header — kept on the row so the money is readable while
                        the card is shut, and printed once instead of twice. */}
                    {deal && (
                        <p className="text-[11px] truncate">
                            <span className="font-black text-slate-700 tabular-nums">{usd(deal.amount_usd)}</span>
                            <span className="text-slate-400"> · {t(`campaigns.escrow_${deal.escrow_status}`)}</span>
                        </p>
                    )}
                </button>
                <StatusPill status={pillStatus} label={pillLabel} />
                <button onClick={() => setOpen(!open)}
                    aria-expanded={open}
                    aria-label={t(open ? 'campaigns.hideDetails' : 'campaigns.showDetails')}
                    className="w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 flex-shrink-0 transition">
                    <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
            </div>

            {open && (
                <div className="border-t border-slate-100 bg-slate-50 px-3.5 py-4 space-y-3">
                    {/* The live collaboration comes first: once a deal exists,
                        the brief is reference material and the deal is the
                        thing being worked on. */}
                    {deal && <DealCenter deal={deal} me={me} onUpdated={onUpdated} hideHeader />}

                    {/* What this creator actually said, before the brand's
                        brief — it is their own row and their own pitch is
                        the part they will have forgotten first. */}
                    {application.message && (
                        <section className="rounded-2xl bg-white border border-slate-200 p-4">
                            <h2 className="text-base font-black text-slate-900 mb-1.5">{t('campaigns.yourApplication')}</h2>
                            <p className="text-sm text-slate-600 whitespace-pre-line leading-relaxed">{application.message}</p>
                        </section>
                    )}

                    <CampaignBrief campaign={campaign} t={t} />

                    <button onClick={() => onOpen(campaign)}
                        className="btn-secondary w-full py-2 text-xs">
                        {t('campaigns.openCampaign')}
                    </button>
                </div>
            )}
        </div>
    );
}

function ApplicantRow({ application, campaign, onDecided, onTopUp, t }) {
    const [busy, setBusy] = useState(false);
    const [amount, setAmount] = useState(application.proposed_usd || campaign.budget_usd || 0);
    const [error, setError] = useState('');
    const [shortfall, setShortfall] = useState(null);

    const decide = async (action) => {
        setBusy(true);
        setError('');
        setShortfall(null);
        try {
            const res = await api.post(`/api/campaigns/applications/${application.id}/decide/`,
                { action, amount_usd: amount });
            if (res.status === 200 || res.status === 201) onDecided();
        } catch (err) {
            const data = err.response?.data || {};
            if (data.error === 'insufficient_balance') {
                // Say how far short they are and offer the fix inline — this
                // is the moment a brand actually needs to top up.
                setShortfall(Math.max(0, (data.required_usd || 0) - (data.balance_usd || 0)));
                setError(t('campaigns.insufficientBalance'));
            } else {
                setError(data.error || t('campaigns.saveError'));
            }
        } finally { setBusy(false); }
    };

    return (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-center gap-2.5">
                <Avatar user={application.creator} size="w-9 h-9" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-bold text-slate-800 truncate">{application.creator?.username}</p>
                        <ReputationBadge rating={application.creator?.rating} compact />
                        <AvailabilityChip availability={application.creator?.availability} compact />
                    </div>
                    <p className="text-[11px] text-slate-400">
                        {formatDate(application.created_at)}
                        {application.creator?.followers > 0 && ` · ${application.creator.followers} ${t('campaigns.followers')}`}
                    </p>
                </div>
                <span className={`px-2 py-1 rounded-full text-[10px] font-bold border ${application.status === 'approved'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : application.status === 'rejected'
                        ? 'bg-slate-100 text-slate-500 border-slate-200'
                        : application.status === 'withdrawn'
                            ? 'bg-slate-100 text-slate-400 border-slate-200'
                            : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                    {t(`campaigns.appStatus_${application.status}`)}
                </span>
            </div>

            {application.message && (
                <p className="text-sm text-slate-600 mt-2 bg-slate-50 rounded-lg px-3 py-2 leading-relaxed">
                    {application.message}
                </p>
            )}

            {/* The creator's media kit and prices, attached by the act of
                applying — no PDF changes hands. */}
            <ApplicantKit mediaKit={application.media_kit} rateCard={application.rate_card}
                username={application.creator?.username} />

            {application.status === 'pending' && (
                <div className="mt-3 space-y-2">
                    {/* The creator asked for a number; make it one tap to
                        accept it rather than something to re-type. */}
                    {application.proposed_usd > 0 && Number(amount) !== Number(application.proposed_usd) && (
                        <button type="button" onClick={() => setAmount(application.proposed_usd)}
                            className="text-[11px] font-semibold text-primary-700 hover:underline">
                            {t('campaigns.useProposed')} {usd(application.proposed_usd)}
                        </button>
                    )}
                    <div className="flex items-center gap-2">
                        <label className="text-[11px] text-slate-500 flex-shrink-0">{t('campaigns.escrowAmount')}</label>
                        <div className="relative w-32">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400 pointer-events-none">$</span>
                            <input type="number" min={0} step="0.01" value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="input py-1.5 pl-6 text-sm w-full" />
                        </div>
                    </div>
                    {error && <p className="text-[11px] text-red-600">{error}</p>}
                    {shortfall !== null && (
                        <button type="button" onClick={onTopUp}
                            className="w-full py-2 rounded-xl text-xs font-bold text-primary-700 bg-primary-50 border border-primary-200 hover:bg-primary-100 transition">
                            {t('wallet.depositBtn')} · {t('campaigns.shortBy')} {usd(shortfall)}
                        </button>
                    )}
                    <div className="flex gap-2">
                        <button onClick={() => decide('reject')} disabled={busy}
                            className="flex-1 py-2 rounded-xl text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition">
                            {t('campaigns.reject')}
                        </button>
                        <button onClick={() => decide('approve')} disabled={busy}
                            className="btn-success flex-1 py-2 text-sm flex items-center justify-center gap-2">
                            {busy && <span className="spinner-sm" />}
                            {t('campaigns.approveAndLock')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function OwnedCampaign({ campaign, deals, me, onChanged, onEdit, onOpen, onTopUp, t }) {
    const [open, setOpen] = useState(false);
    const applications = campaign.applications || [];
    const pending = applications.filter(a => a.status === 'pending');
    const campaignDeals = deals.filter(d => d.campaign === campaign.id);

    const archive = async () => {
        if (!window.confirm(t('campaigns.archiveConfirm'))) return;
        try {
            await api.delete(`/api/campaigns/${campaign.id}/`);
            onChanged();
        } catch (err) {
            if (err.response?.status === 409) alert(t('campaigns.archiveBlocked'));
        }
    };

    return (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
            <div className="flex items-center gap-3 p-3.5">
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                    {campaign.cover_image
                        ? <img src={campaign.cover_image} alt="" className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-slate-300 text-xl">◎</div>}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                        <h3 className="text-sm font-bold text-slate-800 truncate">{campaign.title}</h3>
                        {campaign.status === 'draft' && (
                            <span className="px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[9px] font-bold">
                                {t('campaigns.draft')}
                            </span>
                        )}
                    </div>
                    {/* The brand's own row reads the real `approved_count`, not
                        `filledSlots`. The applicant list it expands into is
                        directly underneath: a derived figure here would sit
                        one line above the rows that contradict it, and the
                        owner of the campaign is the last person who should be
                        told they hired someone they did not. */}
                    <p className="text-[11px] text-slate-400 mt-0.5">
                        {campaign.applicant_count} {t('campaigns.applicants')} · {campaign.approved_count}/{campaign.slots} {t('campaigns.approved')}
                    </p>
                    {pending.length > 0 && (
                        <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold border border-amber-200">
                            {pending.length} {t('campaigns.newApplications')}
                        </span>
                    )}
                </div>
                <button onClick={() => setOpen(!open)}
                    className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 transition flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
            </div>

            {open && (
                <div className="px-3.5 pb-3.5 space-y-3 border-t border-slate-100 pt-3">
                    <div className="flex gap-2">
                        <button onClick={() => onOpen(campaign)} className="btn-secondary flex-1 py-2 text-xs">
                            {t('campaigns.preview')}
                        </button>
                        <button onClick={() => onEdit(campaign)} className="btn-secondary flex-1 py-2 text-xs">
                            {t('campaigns.edit')}
                        </button>
                        <button onClick={archive}
                            className="px-3 py-2 rounded-xl text-xs font-semibold text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 transition">
                            {t('campaigns.archive')}
                        </button>
                    </div>

                    {campaignDeals.length > 0 && (
                        <div className="space-y-2">
                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">
                                {t('campaigns.activeCollabs')}
                            </p>
                            {campaignDeals.map(deal => (
                                <DealCenter key={deal.id} deal={deal} me={me} onUpdated={onChanged} />
                            ))}
                        </div>
                    )}

                    {/* Who else could fill this brief. Sits above the applicant
                        list because it answers the same question — who works on
                        this — for the people who have not applied yet. */}
                    <CampaignSuggestions campaignId={campaign.id} />

                    <div className="space-y-2">
                        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">
                            {t('campaigns.applicantsTitle')}
                        </p>
                        {applications.length === 0
                            ? <p className="text-xs text-slate-400 italic py-2">{t('campaigns.noApplicants')}</p>
                            : applications.map(application => (
                                <ApplicantRow key={application.id} application={application} campaign={campaign}
                                    onDecided={onChanged} onTopUp={onTopUp} t={t} />
                            ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function EmptyState({ icon, title, desc, action }) {
    return (
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-300 mb-3">
                {icon}
            </div>
            <p className="text-sm font-bold text-slate-600">{title}</p>
            <p className="text-xs text-slate-400 mt-1 max-w-xs leading-relaxed">{desc}</p>
            {action}
        </div>
    );
}

function Campaigns() {
    const { user, refreshUser } = useContext(UserContext);
    const { t } = useTranslation();

    const isBrand = user?.account_type === 'brand';
    const isCreator = user?.account_type === 'creator';

    const [tab, setTab] = useState(isBrand ? 'mine' : 'showcase');
    const [showcase, setShowcase] = useState([]);
    const [mine, setMine] = useState({ campaigns: [], applications: [], deals: [] });
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const [depositing, setDepositing] = useState(false);

    // Showcase filters
    const [query, setQuery] = useState('');
    const [platform, setPlatform] = useState('');
    const [paymentModel, setPaymentModel] = useState('');
    const [openOnly, setOpenOnly] = useState(true);

    const load = useCallback(async () => {
        try {
            const params = {};
            if (query) params.q = query;
            if (platform) params.platform = platform;
            if (paymentModel) params.payment_model = paymentModel;
            if (openOnly) params.open_only = 'true';

            const [showcaseRes, mineRes] = await Promise.all([
                api.get('/api/campaigns/', { params }),
                api.get('/api/campaigns/mine/'),
            ]);
            setShowcase(showcaseRes.data || []);
            setMine(mineRes.data || { campaigns: [], applications: [], deals: [] });
        } catch (err) { /* the interceptor already surfaces auth/payment errors */ }
        finally { setLoading(false); }
    }, [query, platform, paymentModel, openOnly]);

    useEffect(() => {
        const timer = setTimeout(load, query ? 350 : 0);   // debounce the search box
        return () => clearTimeout(timer);
    }, [load, query]);

    // Keep the open detail sheet in sync with freshly loaded data.
    useEffect(() => {
        if (!detail) return;
        const fresh = showcase.find(c => c.id === detail.id) || mine.campaigns.find(c => c.id === detail.id);
        if (fresh && fresh !== detail) setDetail(fresh);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showcase, mine]);

    const tabs = useMemo(() => {
        const list = [];
        if (isBrand) list.push({ key: 'mine', label: t('campaigns.tabMine') });
        if (isCreator) list.push({ key: 'applied', label: t('campaigns.tabApplied') });
        list.push({ key: 'showcase', label: t('campaigns.tabShowcase') });
        // Discovery is the mirror of the showcase: brands browse creators,
        // creators get campaigns matched to what they already published.
        list.push({
            key: 'discover',
            label: isBrand ? t('campaigns.tabDiscover') : t('campaigns.tabForYou'),
        });
        list.push({ key: 'calendar', label: t('campaigns.tabCalendar') });
        // Last on purpose: it is the drawer people open when they already know
        // what they are after, not part of the daily loop.
        list.push({ key: 'contracts', label: t('campaigns.tabContracts') });
        return list;
    }, [isBrand, isCreator, t]);

    const pendingCount = mine.campaigns.reduce(
        (total, campaign) => total + (campaign.applications || []).filter(a => a.status === 'pending').length, 0);

    if (loading) {
        return <div className="flex items-center justify-center h-screen"><div className="spinner" /></div>;
    }

    return (
        <div className="min-h-screen bg-slate-50 pb-28">
            <div className="max-w-5xl mx-auto px-3 sm:px-4 pt-4 sm:pt-6">

                {/* Header */}
                <div className="flex items-center justify-between gap-3 mb-4">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-black text-slate-900">{t('campaigns.pageTitle')}</h1>
                        <p className="text-xs text-slate-500">{t('campaigns.pageSubtitle')}</p>
                    </div>
                    {isBrand && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {/* The wallet is what funds an approval, so it belongs
                                in view before the brand starts reviewing. */}
                            <button onClick={() => setDepositing(true)}
                                className="px-3 py-2 rounded-xl border border-primary-200 bg-primary-50 hover:bg-primary-100 transition text-left">
                                <span className="block text-[9px] font-bold text-primary-600 uppercase tracking-wide leading-none">
                                    {t('wallet.spendable')}
                                </span>
                                <span className="block text-sm font-black text-primary-800 tabular-nums leading-tight">
                                    {usd(user?.balance_usd)}
                                </span>
                            </button>
                            <button onClick={() => setCreating(true)}
                                className="btn-primary py-2.5 px-4 text-sm font-bold flex items-center gap-1.5">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                </svg>
                                <span className="hidden sm:inline">{t('campaigns.newCampaign')}</span>
                            </button>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="flex bg-white rounded-2xl p-1 mb-4 border border-slate-200 shadow-sm">
                    {tabs.map(item => (
                        <button key={item.key} onClick={() => setTab(item.key)}
                            className={`flex-1 text-sm font-bold py-2.5 rounded-xl transition-all duration-200 relative ${tab === item.key
                                ? 'bg-primary-600 text-white shadow-sm'
                                : 'text-slate-500 hover:bg-slate-50'}`}>
                            {item.label}
                            {item.key === 'mine' && pendingCount > 0 && (
                                <span className="absolute top-1 right-2 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center">
                                    {pendingCount}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* ── Showcase ── */}
                {tab === 'showcase' && (
                    <>
                        <div className="rounded-2xl bg-white border border-slate-200 p-3 mb-4 space-y-2">
                            <input className="input py-2 text-sm" value={query}
                                placeholder={t('campaigns.searchPlaceholder')}
                                onChange={(e) => setQuery(e.target.value)} />
                            <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-0.5">
                                <select className="input py-1.5 text-xs w-auto flex-shrink-0" value={platform}
                                    onChange={(e) => setPlatform(e.target.value)}>
                                    <option value="">{t('campaigns.allPlatforms')}</option>
                                    <PlatformOptions t={t} />
                                </select>
                                <select className="input py-1.5 text-xs w-auto flex-shrink-0" value={paymentModel}
                                    onChange={(e) => setPaymentModel(e.target.value)}>
                                    <option value="">{t('campaigns.allPaymentModels')}</option>
                                    {PAYMENT_MODELS.map(m => <option key={m} value={m}>{t(`campaigns.payment_${m}`)}</option>)}
                                </select>
                                <button onClick={() => setOpenOnly(!openOnly)}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold border flex-shrink-0 transition ${openOnly
                                        ? 'bg-primary-600 text-white border-primary-600'
                                        : 'bg-white text-slate-500 border-slate-200'}`}>
                                    {t('campaigns.openOnly')}
                                </button>
                            </div>
                        </div>

                        {showcase.length === 0 ? (
                            <EmptyState
                                icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>}
                                title={t('campaigns.emptyShowcase')} desc={t('campaigns.emptyShowcaseDesc')} />
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {showcase.map(campaign => (
                                    <CampaignCard key={campaign.id} campaign={campaign} onOpen={setDetail} t={t} />
                                ))}
                            </div>
                        )}
                    </>
                )}

                {/* ── My campaigns (brand) ── */}
                {tab === 'mine' && (
                    mine.campaigns.length === 0 ? (
                        <EmptyState
                            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" /></svg>}
                            title={t('campaigns.emptyMine')} desc={t('campaigns.emptyMineDesc')}
                            action={<button onClick={() => setCreating(true)} className="btn-primary mt-4 py-2.5 px-5 text-sm">
                                {t('campaigns.newCampaign')}
                            </button>} />
                    ) : (
                        <div className="space-y-3">
                            {mine.campaigns.map(campaign => (
                                <OwnedCampaign key={campaign.id} campaign={campaign} deals={mine.deals} me={user}
                                    onChanged={load} onEdit={setEditing} onOpen={setDetail}
                                    onTopUp={() => setDepositing(true)} t={t} />
                            ))}
                        </div>
                    )
                )}

                {/* ── My applications (creator) ── */}
                {tab === 'applied' && (
                    mine.applications.length === 0 ? (
                        <EmptyState
                            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
                            title={t('campaigns.emptyApplied')} desc={t('campaigns.emptyAppliedDesc')}
                            action={<button onClick={() => setTab('showcase')} className="btn-primary mt-4 py-2.5 px-5 text-sm">
                                {t('campaigns.browseShowcase')}
                            </button>} />
                    ) : (
                        <div className="space-y-3">
                            {mine.applications.map(application => (
                                <AppliedRow key={application.id} application={application}
                                    deal={mine.deals.find(d => d.id === application.deal?.id)}
                                    me={user} onOpen={setDetail} onUpdated={load} t={t} />
                            ))}
                        </div>
                    )
                )}

                {/* ── Discovery ── */}
                {tab === 'discover' && (
                    isBrand ? <CreatorDiscovery /> : <CreatorOpportunities onOpen={setDetail} />
                )}

                {/* ── Calendar ── */}
                {tab === 'calendar' && <CampaignCalendar />}

                {/* ── Contracts ── */}
                {tab === 'contracts' && <ContractsList />}
            </div>

            {detail && (
                <CampaignDetail campaign={detail} me={user}
                    onClose={() => setDetail(null)}
                    onChanged={() => { setDetail(null); load(); }} />
            )}

            {(creating || editing) && (
                <CampaignForm campaign={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSaved={() => { setCreating(false); setEditing(null); load(); }} />
            )}

            {depositing && (
                <DepositModal
                    onClose={() => { setDepositing(false); refreshUser(); }}
                    onFunded={() => { refreshUser(); load(); }} />
            )}
        </div>
    );
}

export default Campaigns;
